import React, {useCallback, useMemo} from 'react'
import {ColumnId, IColumnItem} from "@ui/tableCustom/types/columns";
import {
    HideColumnIdsExportParams,
    HideRowsIdsExportParams,
    IExportMutateContext,
    IExportParamsContext
} from "@containers/export/containers/buildData/hooks/types/exportParams";
import {RowData} from "@ui/tableCustom/types/table";

interface IProps<T> {
    fullColumns: IColumnItem<T>[],
    exportParams: IExportParamsContext<T>,
    enabled?: boolean,
    data: RowData<T>[]
}

export function useExportChange<T>({
                                       fullColumns,
                                       exportParams,
                                       enabled,
                                       data,
                                   }: IProps<T>): IExportMutateContext<T> {


    const [rerenderAllRowsDeps, setRerenderAllRowsDeps] = React.useState(false)


    const {setExportMode} = exportParams;

    const closeExportMode = useCallback(() => {
        setExportMode(false)
    }, [])

    const onChangeExportRow = useCallback((id: string | number) => {
        exportParams.setHideRowIds((prev) => {
            const has = prev && (id?.toString() in prev) && prev[id?.toString()]
            return {
                ...prev,
                [id.toString()]: !has
            }
        })
    }, [])

    const onChangeColumn = useCallback((id: ColumnId<T>) => {
        exportParams.setHideColumnIds((prev) => {
            const has = prev && (id?.toString() in prev) && prev[id]
            return {
                ...prev,
                [id.toString()]: !has
            }
        })
    }, [])

    const onSelectAllRow = useCallback(() => {
        exportParams.setHideRowIds({} as HideRowsIdsExportParams)
        setRerenderAllRowsDeps(prev => !prev)
    }, [])

    const onClearAllRow = useCallback(() => {
        const allTargetIds: Record<string, boolean> = {}
        if (data?.length) {
            data.forEach((it) => {
                //@ts-ignore
                allTargetIds[it.id?.toString()] = true
            })
        }
        exportParams.setHideRowIds(allTargetIds)
        setRerenderAllRowsDeps(prev => !prev)
    }, [data])

    const onSelectAllColumn = useCallback(() => {
        exportParams.setHideColumnIds({} as HideColumnIdsExportParams<T>)
    }, [])

    const onClearAllColumn = useCallback(() => {
        const allHideColumnIds: HideColumnIdsExportParams<T> = {} as HideColumnIdsExportParams<T>
        if (fullColumns?.length) {
            fullColumns.forEach((it) => {
                allHideColumnIds[it.id] = true
            })
        }
        exportParams.setHideColumnIds(allHideColumnIds)
    }, [fullColumns])

    const hasExportRow = useCallback((id: string | number) => {

        return !(id in exportParams.hideRowIds && exportParams.hideRowIds[id?.toString()])
    }, [exportParams.hideRowIds])

    const hasExportColumn = useCallback((id: ColumnId<T>) => {
        return !(id in exportParams.hideColumnIds && exportParams.hideColumnIds[id])
    }, [exportParams.hideColumnIds])

    const dataExp: T[] = useMemo(() => {
        if (!data?.length) {
            return []
        }

        if (!Object.keys(exportParams.hideRowIds)?.length) {
            return data
        }
        return data?.filter((item) => {
            return !(item.id?.toString() in exportParams.hideRowIds && exportParams.hideRowIds[item?.id?.toString()])
        })
    }, [data, exportParams.hideRowIds])


    const columnExport: IColumnItem<T>[] = useMemo(() => {
        return fullColumns?.filter((col) => {
            return !(col.id?.toString() in exportParams.hideColumnIds  &&  exportParams.hideColumnIds[col?.id])
        })
    }, [fullColumns, exportParams.hideColumnIds])

    return {
        onSelectAllRow,
        onSelectAllColumn,
        onClearAllColumn,
        onClearAllRow,
        closeExportMode,
        onChangeColumn,

        onChangeExportRow,
        hasExportRow,
        hasExportColumn,
        rerenderAllRowsDeps,

        columnExport,
        dataExp,
    }


}